import * as React from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";

import { useEffect } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

import Menu from "./Menu";
import AppBar from "./AppBar";

const drawerWidth = 300;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

type DrawerLayoutProps = {
  children: React.ReactNode;
  colorMode: any;
};

export default function DrawerLayout({
  children,
  colorMode,
}: DrawerLayoutProps) {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("xl"));
  const isScreenSM = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setOpen(matches);
  }, [matches, colorMode]);

  return (
    <Box sx={{ display: "flex", overflowX: "hidden" }}>
      <CssBaseline />
      <Box
        sx={{
          [theme.breakpoints.down("md")]: { ...(open && { display: "none" }) },
        }}
      >
        <AppBar
          handleDrawerOpen={handleDrawerOpen}
          open={open}        
        />
      </Box>
      <Drawer variant={open ? "permanent" : "temporary"} open={open}>
        <Menu handleDrawerClose={handleDrawerClose} />
      </Drawer>
      {isScreenSM && open ? null : (
        <Box
          component="main"
          sx={{
            flex: 1,
            overflowX: "hidden", // Add 'overflowX: "hidden"'
          }}
        >
          <DrawerHeader />
          <Box
            sx={{
              p: 2,
              overflowX: "hidden",
            }} // Add 'overflowX: "hidden"'
          >
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
}
